import React, { useEffect, useMemo } from 'react';
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Button,
  TextField,
  Grid,
  Alert,
  Typography,
  Box,
} from '@mui/material';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import userService from '../services/userService';
import { useAuth } from '../hooks/useAuth';
import { User } from '../types';

interface ChangePasswordForm {
  currentPassword?: string;
  newPassword: string;
  confirmPassword: string;
}

interface ChangePasswordDialogProps {
  open: boolean;
  onClose: () => void;
  user?: User | null;
}

const ChangePasswordDialog: React.FC<ChangePasswordDialogProps> = ({
  open,
  onClose,
  user,
}) => {
  const queryClient = useQueryClient();
  const { user: currentUser } = useAuth();

  const isChangingOwnPassword = currentUser?.id === user?.id;
  const isAdminReset =
    currentUser?.role === 'admin' && user && !isChangingOwnPassword;

  const schema = useMemo(
    () =>
      yup.object({
        currentPassword: isAdminReset
          ? yup.string().optional()
          : yup.string().required('Password lama wajib diisi'),
        newPassword: yup
          .string()
          .required('Password baru wajib diisi')
          .min(6, 'Password minimal 6 karakter')
          .matches(
            /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/,
            'Password harus mengandung huruf kecil, huruf besar, dan angka'
          ),
        confirmPassword: yup
          .string()
          .required('Konfirmasi password wajib diisi')
          .oneOf([yup.ref('newPassword')], 'Password tidak cocok'),
      }),
    [isAdminReset]
  );

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      currentPassword: '',
      newPassword: '',
      confirmPassword: '',
    },
  });

  useEffect(() => {
    if (open) {
      reset({
        currentPassword: '',
        newPassword: '',
        confirmPassword: '',
      });
    }
  }, [open, reset, isAdminReset]);

  const changePasswordMutation = useMutation({
    mutationFn: ({
      id,
      data,
    }: {
      id: string;
      data: { currentPassword?: string; newPassword: string };
    }) => userService.changePassword(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
      onClose();
      reset();
    },
  });

  const onSubmit = (data: ChangePasswordForm & { confirmPassword?: string }) => {
    if (!user) return;
    const { confirmPassword, currentPassword, newPassword } = data;
    void confirmPassword;

    const payload: { currentPassword?: string; newPassword: string } = {
      newPassword,
    };
    if (!isAdminReset && currentPassword) {
      payload.currentPassword = currentPassword;
    }

    changePasswordMutation.mutate({
      id: user.id,
      data: payload,
    });
  };

  const handleClose = () => {
    onClose();
    reset();
  };

  if (!user) return null;

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Ubah Password</DialogTitle>

      <form onSubmit={handleSubmit(onSubmit)}>
        <DialogContent>
          {changePasswordMutation.error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {(changePasswordMutation.error as { response?: { data?: { message?: string } } })
                .response?.data?.message || 'Gagal mengubah password'}
            </Alert>
          )}

          <Box sx={{ mb: 2 }}>
            <Typography variant="body1" gutterBottom>
              <strong>Pengguna:</strong> {user.name} (@{user.username})
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {isChangingOwnPassword
                ? 'Anda mengubah password akun sendiri.'
                : 'Sebagai admin, Anda dapat mengatur password baru tanpa password lama.'}
            </Typography>
          </Box>

          <Grid container spacing={2}>
            {!isAdminReset && (
              <Grid size={{ xs: 12 }}>
                <Controller
                  name="currentPassword"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label={
                        isChangingOwnPassword
                          ? 'Password Lama Anda'
                          : 'Password Lama'
                      }
                      type="password"
                      fullWidth
                      error={!!errors.currentPassword}
                      helperText={
                        errors.currentPassword?.message ||
                        'Masukkan password lama untuk konfirmasi'
                      }
                    />
                  )}
                />
              </Grid>
            )}

            <Grid size={{ xs: 12 }}>
              <Controller
                name="newPassword"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Password Baru"
                    type="password"
                    fullWidth
                    error={!!errors.newPassword}
                    helperText={errors.newPassword?.message}
                  />
                )}
              />
            </Grid>

            <Grid size={{ xs: 12 }}>
              <Controller
                name="confirmPassword"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Konfirmasi Password Baru"
                    type="password"
                    fullWidth
                    error={!!errors.confirmPassword}
                    helperText={errors.confirmPassword?.message}
                  />
                )}
              />
            </Grid>
          </Grid>

          <Alert severity="info" sx={{ mt: 2 }}>
            <Typography variant="body2">
              <strong>Syarat password:</strong>
            </Typography>
            <Typography variant="body2" component="ul" sx={{ mt: 1, mb: 0 }}>
              <li>Minimal 6 karakter</li>
              <li>Mengandung huruf kecil (a-z)</li>
              <li>Mengandung huruf besar (A-Z)</li>
              <li>Mengandung angka (0-9)</li>
            </Typography>
          </Alert>
        </DialogContent>

        <DialogActions>
          <Button onClick={handleClose} disabled={changePasswordMutation.isPending}>
            Batal
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={changePasswordMutation.isPending}
          >
            {changePasswordMutation.isPending ? 'Menyimpan...' : 'Simpan Password'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default ChangePasswordDialog;
